import React, { useState, useEffect, useRef } from 'react';

const LoadingIndicator = ({ className = '', showText = false, text = "Thinking..." }) => {
  const [pupilPos, setPupilPos] = useState({ x: 0, y: 0 });
  const [isBlinking, setIsBlinking] = useState(false);
  const [pupilScale, setPupilScale] = useState(1);
  const [velocity, setVelocity] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef(null);
  const blinkTimeoutRef = useRef(null);
  const lastPos = useRef({ x: 0, y: 0 });
  
  // Blinking effect - random blinks every 2-5 seconds
  useEffect(() => {
    const scheduleBlink = () => {
      const nextBlink = 2000 + Math.random() * 3000;
      blinkTimeoutRef.current = setTimeout(() => {
        setIsBlinking(true);
        // Blink duration: 100-150ms
        setTimeout(() => setIsBlinking(false), 100 + Math.random() * 50);
        scheduleBlink();
      }, nextBlink);
    };
    
    scheduleBlink();
    
    return () => {
      if (blinkTimeoutRef.current) clearTimeout(blinkTimeoutRef.current);
    };
  }, []);
  
  // Eye movement
  useEffect(() => {
    // Predefined "thinking" positions with personality
    const thinkingPositions = [
      { x: 3, y: -2.5, scale: 1.1 },   // up-right (curious)
      { x: -2.5, y: -2, scale: 1.05 }, // up-left (pondering)
      { x: 0, y: -3, scale: 1.15 },    // straight up (big idea!)
      { x: 3.5, y: 0, scale: 0.95 },   // right (reading)
      { x: -3.5, y: 0, scale: 0.95 },  // left (scanning)
      { x: 0, y: 2, scale: 0.9 },      // down (focused)
      { x: 2, y: 1.5, scale: 1 },      // down-right
      { x: 0, y: 0, scale: 1.05 },     // center (attentive)
    ];
    
    let positionIndex = 0;
    
    const movePupils = () => {
      let newPos;
      let newScale;
      
      // 75% thinking positions, 25% random for organic feel
      if (Math.random() < 0.75) {
        positionIndex = (positionIndex + 1 + Math.floor(Math.random() * 2)) % thinkingPositions.length;
        const pos = thinkingPositions[positionIndex];
        newPos = { x: pos.x, y: pos.y };
        newScale = pos.scale;
      } else {
        const angle = Math.random() * Math.PI * 2;
        const distance = 2 + Math.random() * 2;
        newPos = {
          x: Math.cos(angle) * distance,
          y: Math.sin(angle) * distance * 0.7,
        };
        newScale = 0.95 + Math.random() * 0.2;
      }
      
      // Calculate velocity for squash/stretch effect
      const vel = {
        x: newPos.x - lastPos.current.x,
        y: newPos.y - lastPos.current.y,
      };
      const moveDistance = Math.sqrt(vel.x ** 2 + vel.y ** 2);
      
      setVelocity(vel);
      lastPos.current = newPos;
      
      setPupilPos(newPos);
      setPupilScale(newScale);
      
      // Clear velocity after movement settles (for squash/stretch reset)
      setTimeout(() => setVelocity({ x: 0, y: 0 }), 120);
      
      // Variable hold time: shorter for big moves, longer for small
      const holdTime = moveDistance > 3 ? 300 + Math.random() * 300 : 500 + Math.random() * 600;
      
      timeoutRef.current = setTimeout(movePupils, holdTime);
    };
    
    timeoutRef.current = setTimeout(movePupils, 300);
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  
  // Calculate squash/stretch based on velocity
  const speed = Math.sqrt(velocity.x ** 2 + velocity.y ** 2);
  const stretchAmount = Math.min(speed * 0.08, 0.3);
  const angle = Math.atan2(velocity.y, velocity.x) * (180 / Math.PI);
  
  const pupilStyle = {
    transform: `
      translate(${pupilPos.x}px, ${pupilPos.y}px) 
      scale(${pupilScale}) 
      rotate(${angle}deg) 
      scaleX(${1 + stretchAmount}) 
      scaleY(${1 - stretchAmount * 0.5})
    `,
    transition: 'transform 100ms cubic-bezier(0.34, 1.56, 0.64, 1)', // overshoot easing
  };
  
  const eyeStyle = {
    transform: isBlinking ? 'scaleY(0.1)' : 'scaleY(1)',
    transition: 'transform 80ms ease-in-out',
  };
  
  return (
    <div className={`flex items-center space-x-2 px-0 mt-2 py-4 gap-1.5 rounded-2xl max-w-xs ${className}`}>
      <div className="flex items-center gap-0.5">
        {/* Left Eye */}
        <div 
          className="relative w-5 h-5 rounded-full border-[4px] border-gray-900 dark:border-white bg-transparent flex items-center justify-center overflow-hidden"
          style={eyeStyle}
        >
          <div 
            className="w-1.5 h-1.5 rounded-full bg-emerald-400"
            style={pupilStyle}
          />
        </div>
        
        {/* Right Eye */}
        <div 
          className="relative w-5 h-5 rounded-full border-[4px] border-gray-900 dark:border-white bg-transparent flex items-center justify-center overflow-hidden"
          style={eyeStyle}
        >
          <div 
            className="w-1.5 h-1.5 rounded-full bg-emerald-400"
            style={pupilStyle}
          />
        </div>
      </div>
      
      {showText && (
        <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">{text}</span>
      )}
    </div>
  );
};

export default LoadingIndicator;
